export const STORYBOOK_MAX_FIELD_LENGTHS = {
  childName: 80,
  childGender: 60,
  childPersonality: 1200,
  favoriteActivities: 1200,
  childNeed: 240,
  customNeedDetail: 600,
  storyTheme: 240,
  customStoryWorldDetail: 600,
  educationalFocus: 160,
  moralLesson: 1200,
  mainCharacterType: 80,
  characterTraits: 1200,
  sidekickCharacter: 160,
  specialRequests: 1500,
  parentName: 120,
  parentEmail: 254,
  phoneNumber: 80,
  deliveryPreference: 40
};

export const CHILD_AGE_OPTIONS = [3, 4, 5, 6, 7, 8];
export const CHILD_PHOTO_LIMIT = 5;
export const INSPIRATION_IMAGE_LIMIT = 3;
export const MAX_IMAGE_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_TOTAL_IMAGE_UPLOAD_SIZE_BYTES = 8 * 1024 * 1024;
export const COMPRESSED_IMAGE_MAX_SIZE_MB = 1;
export const IMAGE_UPLOAD_ACCEPT = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif']
};
export const IMAGE_UPLOAD_EXTENSION_BY_MIME = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif'
};
export const IMAGE_UPLOAD_MIME_TYPES = Object.keys(IMAGE_UPLOAD_ACCEPT);
export const IMAGE_UPLOAD_EXTENSIONS = Object.values(IMAGE_UPLOAD_ACCEPT).flat();
export const CUSTOM_NEED_VALUE = 'Custom need';
export const CUSTOM_STORY_WORLD_VALUE = 'Custom story world';
export const DEFAULT_STORY_WORLD_VALUE = 'Gentle everyday story';

export const hasAllowedStorybookImageExtension = (fileName = '') => {
  const normalizedName = String(fileName).toLowerCase();
  return IMAGE_UPLOAD_EXTENSIONS.some((extension) => normalizedName.endsWith(extension));
};

export const isAllowedStorybookImageFile = (file = {}) => {
  const mimeType = String(file.type || '').toLowerCase();

  if (IMAGE_UPLOAD_MIME_TYPES.includes(mimeType)) {
    return true;
  }

  return !mimeType && hasAllowedStorybookImageExtension(file.name);
};

export const SUPPORT_NEEDS = [
  { key: 'startingSchool', value: 'Starting school or daycare' },
  { key: 'confidence', value: 'Confidence with hard things' },
  { key: 'siblingJealousy', value: 'Sibling jealousy or a new baby' },
  { key: 'sleepRoutine', value: 'Sleep routine or bedtime fear' },
  { key: 'fearAnxiety', value: 'Fear, worry, or separation anxiety' },
  { key: 'bigFeelings', value: 'Big feelings and self-regulation' },
  { key: 'sharingFriendship', value: 'Sharing, friendship, or kindness' },
  { key: 'languageDevelopment', value: 'Speech or language development' },
  { key: 'readingMotivation', value: 'Reading motivation' },
  { key: 'learningNew', value: 'Learning something new' },
  { key: 'customNeed', value: CUSTOM_NEED_VALUE }
];

export const SUPPORT_NEED_ROUTE_PARAMS = {
  startingSchool: 'starting-school',
  confidence: 'confidence',
  siblingJealousy: 'sibling-jealousy',
  sleepRoutine: 'sleep-routine',
  fearAnxiety: 'fear-anxiety',
  bigFeelings: 'big-feelings',
  sharingFriendship: 'sharing-friendship',
  languageDevelopment: 'language-development',
  readingMotivation: 'reading-motivation',
  learningNew: 'learning-new',
  customNeed: 'custom-need'
};

const SUPPORT_NEED_VALUE_BY_ROUTE_PARAM = SUPPORT_NEEDS.reduce((valuesByParam, option) => {
  const routeParam = SUPPORT_NEED_ROUTE_PARAMS[option.key];

  return routeParam
    ? { ...valuesByParam, [routeParam]: option.value }
    : valuesByParam;
}, {});

export const getSupportNeedRouteParam = (value) => {
  const option = SUPPORT_NEEDS.find((item) => item.value === value);

  return option ? SUPPORT_NEED_ROUTE_PARAMS[option.key] || '' : '';
};

export const getSupportNeedValueFromRouteParam = (value = '') => {
  const routeParam = String(value || '').trim().toLowerCase();

  return SUPPORT_NEED_VALUE_BY_ROUTE_PARAM[routeParam] || '';
};

export const STORY_WORLDS = [
  { key: 'gentleEveryday', value: DEFAULT_STORY_WORLD_VALUE },
  { key: 'bedtimeComfort', value: 'Bedtime and comfort' },
  { key: 'adventureExploration', value: 'Adventure and exploration' },
  { key: 'animalsNature', value: 'Animals and nature' },
  { key: 'spaceScience', value: 'Space and science' },
  { key: 'friendshipKindness', value: 'Friendship and kindness' },
  { key: 'everydayHeroes', value: 'Everyday heroes' },
  { key: 'culturalFamily', value: 'Cultural or family story' },
  { key: 'customWorld', value: CUSTOM_STORY_WORLD_VALUE }
];

export const EDUCATIONAL_FOCI = [
  { key: 'emotionalIntelligence', value: 'Emotional Intelligence' },
  { key: 'socialSkills', value: 'Social Skills' },
  { key: 'problemSolving', value: 'Problem Solving' },
  { key: 'readingLiteracy', value: 'Reading & Literacy' },
  { key: 'scienceNature', value: 'Science & Nature' },
  { key: 'creativityArts', value: 'Creativity & Arts' },
  { key: 'culturalAwareness', value: 'Cultural Awareness' },
  { key: 'lifeSkills', value: 'Life Skills' },
  { key: 'noneEmotional', value: 'None - focus on the emotional moment' }
];

export const GENDER_OPTIONS = [
  { key: 'unspecified', value: '' },
  { key: 'boy', value: 'boy' },
  { key: 'girl', value: 'girl' },
  { key: 'nonBinary', value: 'non-binary' }
];

export const MAIN_CHARACTER_OPTIONS = [
  { key: 'child', value: 'child' },
  { key: 'animal', value: 'animal' },
  { key: 'fantasy', value: 'fantasy' },
  { key: 'superhero', value: 'superhero' }
];

export const STORY_LENGTH_OPTIONS = [
  { key: 'short', value: 'short' },
  { key: 'medium', value: 'medium' },
  { key: 'long', value: 'long' }
];

const STORY_LENGTH_DISPLAY_LABELS = {
  short: 'Short and gentle',
  medium: 'Balanced',
  long: 'Richer and more detailed'
};

const EDUCATIONAL_FOCUS_DISPLAY_LABELS = {
  emotionalIntelligence: 'Emotional intelligence',
  socialSkills: 'Social skills',
  problemSolving: 'Problem solving',
  readingLiteracy: 'Reading and literacy',
  scienceNature: 'Science and nature',
  creativityArts: 'Creativity and arts',
  culturalAwareness: 'Cultural awareness',
  lifeSkills: 'Life skills',
  noneEmotional: 'None - focus on the emotional moment'
};

const GENDER_DISPLAY_LABELS = {
  unspecified: 'Prefer not to specify',
  boy: 'Boy',
  girl: 'Girl',
  nonBinary: 'Non-binary'
};

const MAIN_CHARACTER_DISPLAY_LABELS = {
  child: 'Your child as the main character',
  animal: 'Animal character inspired by your child',
  fantasy: 'Imaginative character inspired by your child',
  superhero: 'Superhero character'
};

const getOptionDisplayLabel = (options, labelsByKey, value, fallback = '') => {
  const option = options.find((item) => item.value === value);

  return option ? labelsByKey[option.key] || option.value : (fallback || value || '');
};

export const getStoryLengthDisplayLabel = (value) => (
  getOptionDisplayLabel(STORY_LENGTH_OPTIONS, STORY_LENGTH_DISPLAY_LABELS, value, 'Balanced')
);

export const getEducationalFocusDisplayLabel = (value) => (
  value ? getOptionDisplayLabel(EDUCATIONAL_FOCI, EDUCATIONAL_FOCUS_DISPLAY_LABELS, value) : ''
);

export const getGenderDisplayLabel = (value) => (
  value ? getOptionDisplayLabel(GENDER_OPTIONS, GENDER_DISPLAY_LABELS, value) : ''
);

export const getMainCharacterDisplayLabel = (value) => (
  getOptionDisplayLabel(MAIN_CHARACTER_OPTIONS, MAIN_CHARACTER_DISPLAY_LABELS, value, MAIN_CHARACTER_DISPLAY_LABELS.child)
);
